/**
 * Zápasy — prehľad všetkých zápasov všetkých hráčov: online, proti botovi
 * aj hot-seat. Je to len zoznam: do cudzieho zápasu sa nazerať nedá a ID
 * cudzích zápasov server neposiela (odkaz s ID je pozvánka na voľné miesto).
 * Otvoriť sa dajú len moje.
 */

import { useEffect, useState } from 'react';

import {
  ApiError,
  BOT_LEVEL_LABEL,
  matches,
  type MatchMode,
  type MatchStatus,
  type PublicMatch,
} from '../api.js';
import { ago, artOf, heroName, mapName } from './Home.js';

interface Props {
  nick: string;
  onOpenMatch: (id: string) => void;
}

const PAGE = 30;

const MODES: { value: MatchMode | undefined; label: string }[] = [
  { value: undefined, label: 'Všetky' },
  { value: 'online', label: 'Online' },
  { value: 'solo', label: 'Proti botovi' },
  { value: 'hotseat', label: 'Hot-seat' },
];

const STATUSES: { value: MatchStatus | undefined; label: string }[] = [
  { value: undefined, label: 'Všetky' },
  { value: 'playing', label: 'Prebiehajú' },
  { value: 'lobby', label: 'Čakajú na hráčov' },
  { value: 'ended', label: 'Dohrané' },
];

const MODE_LABEL: Record<MatchMode, string> = {
  online: 'Online',
  solo: 'Proti botovi',
  hotseat: 'Hot-seat',
};

const STATUS_LABEL: Record<MatchStatus, string> = {
  lobby: 'Čaká na hráčov',
  playing: 'Prebieha',
  ended: 'Dohraný',
  abandoned: 'Opustený',
};

/** Víťaz je TÍM — v duele a FFA sa zhoduje so slotom, v 2v2 je to slot % 2. */
function slotWon(match: PublicMatch, slot: number): boolean {
  if (match.winner === null) return false;
  return match.format === 'teams' ? slot % 2 === match.winner : slot === match.winner;
}

/** Kto sedí na mieste: hráč, bot (s úrovňou) alebo voľné miesto. */
function seatLabel(match: PublicMatch, slot: number): string {
  const level = match.bot_levels[slot];
  if (level) return `bot · ${BOT_LEVEL_LABEL[level].toLowerCase()}`;
  return match.nicks[slot] ?? 'voľné miesto';
}

export function Matches({ nick, onOpenMatch }: Props) {
  const [mode, setMode] = useState<MatchMode | undefined>(undefined);
  const [status, setStatus] = useState<MatchStatus | undefined>(undefined);
  const [page, setPage] = useState(0);
  const [data, setData] = useState<{ matches: PublicMatch[]; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    void (async () => {
      try {
        const result = await matches.all({ mode, status, limit: PAGE, offset: page * PAGE });
        if (alive) setData(result);
      } catch (err) {
        if (alive) setError(err instanceof ApiError ? err.message : 'Nepodarilo sa načítať zápasy');
      }
    })();
    return () => {
      alive = false;
    };
  }, [mode, status, page]);

  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;

  return (
    <div className="matchesPage">
      <section className="matchesPage__intro">
        <span className="tag tag--primary">Prehľad</span>
        <h1 className="hero__title">Zápasy</h1>
        <p className="hero__lead">
          Všetky zápasy všetkých hráčov — online, proti botovi aj hot-seat. Otvoriť môžeš tie, v
          ktorých hráš.
        </p>
      </section>

      <section className="panel matchesPage__filters">
        <div className="segment" role="group" aria-label="Režim">
          {MODES.map((item) => (
            <button
              key={item.label}
              type="button"
              className={`segment__item ${mode === item.value ? 'is-active' : ''}`}
              onClick={() => {
                setMode(item.value);
                setPage(0);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="segment" role="group" aria-label="Stav">
          {STATUSES.map((item) => (
            <button
              key={item.label}
              type="button"
              className={`segment__item ${status === item.value ? 'is-active' : ''}`}
              onClick={() => {
                setStatus(item.value);
                setPage(0);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
        <span className="matchesPage__count">{data ? `${data.total} zápasov` : 'Načítavam…'}</span>
      </section>

      {error ? <p className="formError">{error}</p> : null}

      <div className="matchList">
        {data?.matches.length === 0 ? <p className="muted">Nič tu nie je.</p> : null}
        {data?.matches.map((match) => (
          <MatchRow key={match.key} match={match} nick={nick} onOpen={onOpenMatch} />
        ))}
      </div>

      {pages > 1 ? (
        <nav className="matchesPage__pager" aria-label="Strany">
          <button type="button" className="btn btn--sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
            ← Novšie
          </button>
          <span>
            {page + 1} / {pages}
          </span>
          <button
            type="button"
            className="btn btn--sm"
            disabled={page + 1 >= pages}
            onClick={() => setPage(page + 1)}
          >
            Staršie →
          </button>
        </nav>
      ) : null}
    </div>
  );
}

function MatchRow({ match, nick, onOpen }: { match: PublicMatch; nick: string; onOpen: (id: string) => void }) {
  const slots = match.heroes.map((_, slot) => slot);
  const ended = match.status === 'ended';
  const openable = match.mine && match.id;
  const Tag = openable ? 'button' : 'div';

  return (
    <Tag
      {...(openable ? { type: 'button' as const, onClick: () => onOpen(match.id!) } : {})}
      className={`matchRow${openable ? ' is-mine' : ''}`}
    >
      <span className="matchRow__meta">
        <span className={`matchRow__status matchRow__status--${match.status}`}>{STATUS_LABEL[match.status]}</span>
        <span className="matchRow__mode">{MODE_LABEL[match.mode]}</span>
        <span className="matchRow__map">
          {mapName(match.map_id)}
          {match.format === 'teams' ? ' · 2v2' : match.format === 'ffa' ? ' · každý za seba' : ''}
        </span>
      </span>

      <span className="matchRow__seats">
        {slots.map((slot) => {
          const won = ended && slotWon(match, slot);
          const art = artOf(match.heroes[slot]);
          return (
            <span
              key={slot}
              className={`matchSeat matchSeat--p${slot}${won ? ' is-winner' : ''}${ended && !won ? ' is-loser' : ''}`}
            >
              <span className="matchSeat__art">{art ? <img src={art} alt="" loading="lazy" draggable={false} /> : null}</span>
              <span className="matchSeat__text">
                <b>{heroName(match.heroes[slot])}</b>
                <small className={match.nicks[slot] === nick ? 'is-you' : ''}>{seatLabel(match, slot)}</small>
              </span>
              {won ? <span className="matchSeat__win">víťaz</span> : null}
            </span>
          );
        })}
      </span>

      <span className="matchRow__time">
        {match.turn ? `ťah ${match.turn} · ` : ''}
        {ago(ended && match.ended_at ? match.ended_at : match.updated_at)}
        {openable ? <span className="matchRow__open">Otvoriť →</span> : null}
      </span>
    </Tag>
  );
}
